import React, { useState, useEffect, useContext } from 'react'
import useBreakpoint from '#src/app/hooks/useBreakpoint'
import classNames from 'classnames'
import { Grid, GridItem } from '@deal/components'
import { SpotlightExpertFragment } from '../../SpotlightExperts.generated'
import ArrowLeft from '../../arrow-left.svg'
import ArrowRight from '../../arrow-right.svg'
import DismissIcon from '../../dismiss.svg'
import styles from './styles.css'
import { AnalyticsContext } from '#src/app/containers/Analytics'
import { ConsumerTracking } from '@deal/web-tracking/types'

interface ExpertPlayerProps {
  onNextExpert: () => void
  onPreviousExpert: () => void
  canGoToPrevExpert: boolean
  canGoToNextExpert: boolean
  featuredExpert?: SpotlightExpertFragment
  onDismissVideo: () => void
}

interface HeaderTextProps {
  featuredExpert: SpotlightExpertFragment
}

const HeaderText: React.FC<HeaderTextProps> = ({ featuredExpert }) => {
  const expertTextArr = [
    featuredExpert.displayName,
    featuredExpert.expertProfile ? featuredExpert.expertProfile.spotlightHeadline : ''
  ]

  return (
    <>
      {expertTextArr.map((text, index) => {
        return (
          <div key={index} className={index === 0 ? styles.expertName : styles.expertBody}>
            {text}
          </div>
        )
      })}
    </>
  )
}

const ExpertPlayer: React.FC<ExpertPlayerProps> = ({
  featuredExpert,
  onDismissVideo,
  onNextExpert,
  onPreviousExpert,
  canGoToNextExpert,
  canGoToPrevExpert
}) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const [isOverlayContentHidden, setIsOverlayContentHidden] = useState(false)
  const [hasComponentMounted, setHasComponentMounted] = useState(false)
  const analytics = useContext(AnalyticsContext)

  useEffect(() => {
    setHasComponentMounted(true)
  }, [])

  useEffect(() => {
    if (hasComponentMounted) {
      let overlayContentTimeout = setTimeout(() => {
        setIsOverlayContentHidden(true)
      }, 2000)

      let animationCompleteTimeout = setTimeout(() => {
        setIsAnimationComplete(true)
      }, 3000)

      return () => {
        clearTimeout(overlayContentTimeout)
        clearTimeout(animationCompleteTimeout)
      }
    }
  }, [hasComponentMounted])

  if (
    !featuredExpert ||
    !featuredExpert.expertProfile ||
    !featuredExpert.expertProfile.introductionVideoLink
  ) {
    throw new Error('Attempted to load featured expert player without introduction video')
  }

  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanMd = useBreakpoint('md')
  const isLargerThanLg = useBreakpoint('lg')

  // Start with the smallest video source and work towards the largest
  let videoSource = featuredExpert.expertProfile.introductionVideoLink.smallUrl
  if (isLargerThanSm) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.mediumUrl
  }

  if (isLargerThanMd) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.largeUrl
  }

  if (isLargerThanLg) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.xLargeUrl
  }

  const getVideoPlaybackTrackingInput = (): ConsumerTracking.ExpertIntroductionVideo => {
    const vid = document.getElementById('expert-introduction-video') as HTMLVideoElement
    return {
      type: 'expert-introduction',
      video_url: videoSource,
      duration: Math.round(vid.duration),
      time: Math.round(vid.currentTime),
      expert_id: featuredExpert.id
    }
  }

  return (
    <div className={styles.player}>
      <div className={styles.videoContainer}>
        <div className={styles.mobilePlayerBar}>
          <div>
            <button className={styles.mobileNavigationButton} onClick={() => onDismissVideo()}>
              <DismissIcon width={22} height={22} />
            </button>
          </div>
          <div className={styles.mobileNavigationArrows}>
            {canGoToPrevExpert && (
              <button className={styles.mobileNavigationButton} onClick={() => onPreviousExpert()}>
                <ArrowLeft width={10} height={15} />
              </button>
            )}
            {canGoToNextExpert && (
              <button className={styles.mobileNavigationButton} onClick={() => onNextExpert()}>
                <ArrowRight width={10} height={15} />
              </button>
            )}
          </div>
        </div>
        <video
          id="expert-introduction-video"
          playsInline
          controls
          autoPlay
          className={styles.video}
          src={videoSource}
          onPlay={() =>
            analytics?.events.trackVideoPlaybackStarted(getVideoPlaybackTrackingInput())
          }
          onPause={() =>
            analytics?.events.trackVideoPlaybackPaused(getVideoPlaybackTrackingInput())
          }
          onEnded={() => {
            analytics?.events.trackVideoPlaybackCompleted(getVideoPlaybackTrackingInput())
            if (canGoToNextExpert) {
              onNextExpert()
            } else {
              onDismissVideo()
            }
          }}
          controlsList="nodownload"
        />
      </div>

      <div
        className={classNames(styles.videoOverlay, {
          [styles.hide]: isAnimationComplete
        })}
      >
        <Grid>
          <GridItem col={2} className={styles.gridWrapper}>
            <div
              className={classNames(styles.copy, {
                [styles.hide]: isOverlayContentHidden
              })}
            >
              {hasComponentMounted && <HeaderText featuredExpert={featuredExpert} />}
            </div>
          </GridItem>
        </Grid>
      </div>
    </div>
  )
}

export default ExpertPlayer
